import { z } from "zod";
import { uuid } from "./common";

/** Контракт кассы Kafe Coco (docs/tz_desktop.docx). */
export const pairingCodeSchema = z.strictObject({ venueId: uuid.optional(), name: z.string().trim().max(60).nullable().optional() });
export const terminalPairSchema = z.strictObject({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{8}$/, { error: "Код активации — 8 символов" }),
  name: z.string().trim().max(60).nullable().optional(),
  platform: z.string().trim().max(50).nullable().optional(),
  appVersion: z.string().trim().max(30).nullable().optional(),
});
export const terminalPatchSchema = z.strictObject({
  name: z.string().trim().min(1).max(60).optional(),
  /** Оборудование кассы: принтеры, весы, сканеры — JSON-объект (до 64 КБ) */
  devices: z.record(z.string(), z.unknown()).refine((v) => JSON.stringify(v).length <= 65_536, { error: "Слишком большой объект" }).optional(),
});
export const pullQuery = z.object({
  cursor: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(500),
});
export const venuePatchSchema = z.strictObject({
  name: z.string().trim().min(1).max(120).optional(),
  timezone: z.string().refine((tz) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, { error: "Неизвестный часовой пояс" }).optional(),
  currency: z.strictObject({
    code: z.string().regex(/^[A-Z]{3}$/, { error: "Код валюты ISO 4217" }),
    symbol: z.string().trim().min(1).max(8).optional(),
    symbolPosition: z.enum(["before", "after"]).optional(),
  }).optional(),
  /** Базисные пункты: 1000 = 10% */
  taxRateBp: z.number().int().min(0).max(10_000).optional(),
  taxIncluded: z.boolean().optional(),
});
export type VenuePatch = z.infer<typeof venuePatchSchema>;

// ───────────── push (этап 2) ─────────────

const iso = z.iso.datetime({ offset: true, error: "Время в ISO 8601" });
const minor = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const signed = z.number().int().min(-1_000_000_000_000).max(1_000_000_000_000);
const text = (max: number) => z.string().trim().max(max).nullable().optional();
/** Сверка итогов заказа с расчётом сервера (расхождение → rejected: totals_mismatch) */
const totals = { subtotalMinor: minor.optional(), taxMinor: minor.optional(), totalMinor: minor.optional() };

export const PUSH_MAX = 200;
export const PUSH_ENTITIES = ["order", "payment", "refund", "shift", "reservation"] as const;

/** Мутация: mutationId (UUID v7), entity, id, op, baseVersion, data, clientTime; employeeId — сотрудник за кассой. */
export const mutationSchema = z.object({
  mutationId: uuid,
  entity: z.enum(PUSH_ENTITIES),
  id: uuid,
  op: z.string().min(1).max(30),
  baseVersion: z.number().int().min(0).optional(),
  clientTime: iso.optional(),
  employeeId: uuid.optional(),
  data: z.record(z.string(), z.unknown()).default({}),
});
export type Mutation = z.output<typeof mutationSchema>;
/** Длина пачки проверяется отдельно (> 200 → 413 batch_too_large). */
export const pushSchema = z.strictObject({ batchId: uuid.optional(), mutations: z.array(z.unknown()).min(1) });

export const ORDER_TYPES_CONTRACT = ["dine_in", "take_away", "delivery"] as const;
const header = {
  type: z.enum(ORDER_TYPES_CONTRACT).optional(),
  mode: z.enum(["purchase", "reservation"]).optional(),
  tableIds: z.array(uuid).max(10).optional(),
  customerName: text(120),
  note: text(500),
  /** Coca: клиент CRM */
  customerId: uuid.nullable().optional(),
  /** Coca: число гостей */
  guestsCount: z.number().int().min(1).max(500).nullable().optional(),
};
/** Coca: код причины (BR-042) — для отчётов по отменам */
const reasonCode = z.enum(["guest_mistake", "waiter_mistake", "long_wait", "out_of_stock", "quality", "other"]).optional();
export const addLineData = z.strictObject({
  lineId: uuid,
  productId: uuid,
  nameSnapshot: z.string().trim().min(1).max(200),
  unitPriceMinor: minor,
  /** 1 шт = 1000, 0,356 кг = 356 */
  qtyMilli: z.number().int().min(1).max(1_000_000_000),
  note: text(300),
  // ── Расширение Coca (веб-касса, KDS): необязательно для кассы Kafe Coco ──
  variantId: uuid.nullable().optional(),
  /** Снимки модификаторов; unitPriceMinor строки — без них, цена единицы = unitPriceMinor + Σ модификаторов */
  modifiers: z.array(z.strictObject({
    modifierId: uuid,
    nameSnapshot: z.string().trim().min(1).max(120),
    /** Может быть отрицательной («без сыра −30») */
    unitPriceMinor: signed,
    qty: z.number().int().min(1).max(20).default(1),
    /** С учётом бесплатных единиц группы; по умолчанию unitPriceMinor × qty */
    totalMinor: signed.optional(),
  })).max(50).optional(),
  course: z.number().int().min(1).max(5).optional(),
});
export const pushData = {
  order: {
    create: z.strictObject({
      ...header,
      type: z.enum(ORDER_TYPES_CONTRACT),
      number: z.string().trim().min(1).max(20).optional(),
      currency: z.string().regex(/^[A-Z]{3}$/).optional(),
      shiftId: uuid.nullable().optional(),
      lines: z.array(addLineData).max(200).optional(),
      ...totals,
    }),
    /** Расширение Coca: `waiterId` — передать заказ другому официанту (право `orders.transfer`) */
    update: z.strictObject({ ...header, billRequested: z.boolean().optional(), waiterId: uuid.optional(), ...totals }),
    addLine: addLineData,
    /** Приращение количества (не итоговое число): +1000 / −1000 */
    changeQty: z.strictObject({ lineId: uuid, deltaMilli: z.number().int().min(-1_000_000_000).max(1_000_000_000).refine((v) => v !== 0, { error: "Приращение не может быть 0" }) }),
    voidLine: z.strictObject({ lineId: uuid, reason: text(200), reasonCode }),
    cancel: z.strictObject({ reason: text(200), reasonCode }),
    /** Coca (addon.promotions_pro): включить акции заказа и промокоды — сервер пересчитает итоги; enabled=false — снять */
    promotions: z.strictObject({ enabled: z.boolean(), codes: z.array(z.string().trim().toUpperCase().max(20)).max(5).default([]) }),
    /** Coca: отправить новые строки на кухню (KDS); без lineIds — все неотправленные */
    sendToKitchen: z.strictObject({ lineIds: z.array(uuid).max(200).optional() }),
    /**
     * Coca: ручная скидка на заказ (FR-POS-010): процент (0–100, до сотых) или сумма в minor units; none — снять.
     * Выше лимита роли (max_discount_percent) — rejected: approval_required (BR-032).
     */
    discount: z
      .strictObject({ kind: z.enum(["percent", "amount", "none"]), value: z.number().min(0).max(1e12).optional(), reason: text(200), ...totals })
      .refine((d) => d.kind === "none" || (d.value !== undefined && d.value > 0), { error: "Укажите размер скидки", path: ["value"] })
      .refine((d) => d.kind !== "percent" || (d.value ?? 0) <= 100, { error: "Скидка не больше 100%", path: ["value"] })
      .refine((d) => d.kind !== "percent" || Math.abs((d.value ?? 0) * 100 - Math.round((d.value ?? 0) * 100)) < 1e-6, { error: "До сотых процента", path: ["value"] })
      .refine((d) => d.kind !== "amount" || Number.isInteger(d.value), { error: "Сумма в тыйынах — целое число", path: ["value"] }),
  },
  payment: {
    create: z.strictObject({
      orderId: uuid,
      /** Coca: qr (ручное подтверждение) и credit (в долг, нужен клиент — BR-075) */
      /** deposit — зачёт депозита банкета (только в заказ банкета, не больше внесённого) */
      method: z.enum(["cash", "card", "qr", "credit", "deposit"]),
      amountMinor: minor.refine((v) => v > 0, { error: "Сумма оплаты больше 0" }),
      /** Карта/QR вручную: последние 4 цифры или RRN (FR-PAY-006) */
      reference: z.string().trim().max(40).nullable().optional(),
      /** Клиент для оплаты в долг; по умолчанию — клиент заказа */
      customerId: uuid.optional(),
      tipMinor: minor.default(0),
      changeMinor: minor.default(0),
      paidAt: iso.optional(),
      currency: z.string().regex(/^[A-Z]{3}$/).optional(),
      shiftId: uuid.nullable().optional(),
      fiscal: z.record(z.string(), z.unknown()).nullable().optional(),
      /** Итог заказа по расчёту кассы — сверяется с сервером */
      orderTotalMinor: minor.optional(),
    }),
  },
  /** Возврат — отдельной операцией (контракт), права admin/owner; неизменяем. id мутации = id возврата. */
  refund: {
    create: z.strictObject({
      orderId: uuid,
      /** Оплата, по которой возврат; без неё — способ method */
      paymentId: uuid.optional(),
      method: z.enum(["cash", "card", "qr", "credit"]).optional(),
      amountMinor: minor.refine((v) => v > 0, { error: "Сумма возврата больше 0" }),
      reason: z.string().trim().min(1, { error: "Укажите причину" }).max(300),
      lineIds: z.array(uuid).max(200).optional(),
      refundedAt: iso.optional(),
      shiftId: uuid.nullable().optional(),
      fiscal: z.record(z.string(), z.unknown()).nullable().optional(),
    }).refine((v) => !!v.paymentId || !!v.method, { error: "Укажите оплату или способ возврата", path: ["method"] }),
  },
  shift: {
    open: z.strictObject({ openedAt: iso.optional(), cashStartMinor: minor.default(0) }),
    close: z.strictObject({ closedAt: iso.optional(), cashEndMinor: minor, comment: text(500) }),
  },
  reservation: {
    create: z.strictObject({
      tableIds: z.array(uuid).max(10).default([]),
      customerName: z.string().trim().min(1).max(120),
      phone: text(30),
      guests: z.number().int().min(1).max(500),
      startsAt: iso,
      endsAt: iso,
      status: z.enum(["booked", "seated", "completed", "cancelled", "no_show"]).optional(),
      note: text(500),
    }).refine((v) => Date.parse(v.endsAt) > Date.parse(v.startsAt), { error: "Окончание позже начала", path: ["endsAt"] }),
    update: z.strictObject({
      tableIds: z.array(uuid).max(10).optional(),
      customerName: z.string().trim().min(1).max(120).optional(),
      phone: text(30),
      guests: z.number().int().min(1).max(500).optional(),
      startsAt: iso.optional(),
      endsAt: iso.optional(),
      status: z.enum(["booked", "seated", "completed", "cancelled", "no_show"]).optional(),
      note: text(500),
    }),
    cancel: z.strictObject({ reason: text(200) }),
  },
} as const;

/** Закрытие смены через REST (контракт: POST /shifts/{id}/close). */
export const shiftCloseSchema = z.strictObject({ cashEndMinor: minor, closedAt: iso.optional(), employeeId: uuid.optional(), comment: text(500) });

// ───────────── отчёты и брони (этап 3) ─────────────

/** GET /reports/sales?from=&to=&groupBy=hour|day — период в часовом поясе заведения (даты включительно). */
export const salesReportQuery = z.object({
  from: z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" }),
  to: z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" }),
  groupBy: z.enum(["hour", "day"]).default("day"),
  top: z.coerce.number().int().min(1).max(50).default(10),
}).refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 366 * 86_400_000, { error: "Период не больше года", path: ["to"] });

/** GET /reservations?from=&to=&status= */
export const reservationsQuery = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  status: z.string().max(100).optional(),
  cursor: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
